import { useLayoutEffect, useRef, useState } from "react";
import type { BannerStyle, PlacementInfo } from "../lib/services/banners";
import "../banner-preview.css";

/**
 * The Homepage Banners editor's live preview: the storefront's own banner markup
 * (steelyard-portal/components/storefront-banner.tsx), same class names, drawn with
 * `banner-preview.css` — a copy of the portal's banner rules. It is laid out at the slot's real width
 * in CSS pixels and scaled down to fit, so text sized in container units keeps the proportions the
 * homepage gives it; the frame takes the slot's shape, or the image's own where the slot follows it.
 * Approximate by design: the homepage's exact height also depends on the row around the slot.
 */

const TONE = { dark: "sy-banner-on-dark", light: "sy-banner-on-light" } as const;
const TITLE_SIZE = { small: "sy-ts-s", medium: "sy-ts-m", large: "sy-ts-l", x_large: "sy-ts-xl" } as const;
const TEXT_SIZE = { small: "sy-ss-s", medium: "sy-ss-m", large: "sy-ss-l", x_large: "sy-ss-xl" } as const;
const WEIGHT = { medium: "sy-fw-medium", semibold: "sy-fw-semibold", bold: "sy-fw-bold" } as const;
const PAD = { compact: "sy-pad-compact", normal: "sy-pad-normal", spacious: "sy-pad-spacious" } as const;
const ALIGN = { left: "sy-align-left", center: "sy-align-center", right: "sy-align-right" } as const;
const VALIGN = { top: "sy-valign-top", center: "sy-valign-center", bottom: "sy-valign-bottom" } as const;
const FOCUS = { left: "sy-focus-left", center: "sy-focus-center", right: "sy-focus-right" } as const;
const OVERLAY = { none: "", gradient: "sy-ov-gradient", solid: "sy-ov-solid" } as const;
const OVERLAY_FROM = { left: "sy-ovd-left", right: "sy-ovd-right", top: "sy-ovd-top", bottom: "sy-ovd-bottom", center: "sy-ovd-middle" } as const;

export type PreviewMode = "desktop" | "phone";

export function BannerPreview({
  placement,
  mode,
  desktopSrc,
  mobileSrc,
  title,
  subtitle,
  ctaText,
  tone,
  style,
}: {
  placement: PlacementInfo;
  mode: PreviewMode;
  desktopSrc: string | null;
  mobileSrc: string | null;
  title: string;
  subtitle: string;
  ctaText: string;
  tone: "dark" | "light";
  style: BannerStyle;
}) {
  const frame = useRef<HTMLDivElement>(null);
  const [frameWidth, setFrameWidth] = useState<number | null>(null);
  const [natural, setNatural] = useState<{ src: string; ratio: number } | null>(null);

  useLayoutEffect(() => {
    const el = frame.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => entry && setFrameWidth(entry.contentRect.width));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const phone = mode === "phone" ? placement.phone : null;
  const src = mode === "phone" ? (mobileSrc ?? desktopSrc) : desktopSrc;
  const width = phone ? phone.width : placement.desktopWidth;
  const shape = mode === "phone" ? phone?.shape : placement.shape;
  const slotRatio = placement.recommended.w / placement.recommended.h;
  const ratio =
    typeof shape === "number" ? shape : shape === "image" && natural && natural.src === src ? natural.ratio : slotRatio;
  const scale = frameWidth ? frameWidth / width : 1;

  const showText = placement.showsText;
  const button = ctaText.trim() && style.showButton ? ctaText.trim() : "";
  const hasText = showText && (title.trim() || subtitle.trim() || button);
  const empty =
    mode === "phone" && !placement.phone
      ? `Not shown on phones — this slot appears on ${placement.shownFrom ?? "wider screens"}.`
      : !src
        ? "Upload a desktop image to see the preview."
        : null;

  return (
    <div className="sy-preview" data-testid="banner-preview">
      <div
        ref={frame}
        className="sy-preview-frame"
        style={{ aspectRatio: String(ratio), maxWidth: mode === "phone" ? width : undefined }}
      >
        {empty ? (
          <p className="sy-preview-empty">{empty}</p>
        ) : (
          <div className="sy-preview-stage" style={{ width, height: width / ratio, transform: `scale(${scale})` }}>
            <div className="sy-banner">
              <img
                src={src!}
                alt=""
                className={`${FOCUS[style.imageFocus]}`}
                onLoad={(event) =>
                  setNatural({ src: src!, ratio: event.currentTarget.naturalWidth / (event.currentTarget.naturalHeight || 1) })
                }
              />
              {style.overlay !== "none" && style.overlayOpacity > 0 ? (
                <span
                  data-testid="banner-preview-overlay"
                  className={`sy-banner-overlay ${TONE[tone]} ${OVERLAY[style.overlay]} sy-ov-${style.overlayOpacity} ${OVERLAY_FROM[style.align === "center" ? style.verticalAlign : style.align]}`}
                />
              ) : null}
              {hasText ? (
                <span
                  data-testid="banner-preview-caption"
                  className={`sy-banner-caption ${TONE[tone]} ${PAD[style.padding]} ${ALIGN[style.align]} ${VALIGN[style.verticalAlign]} ${TITLE_SIZE[style.titleSize]} ${TEXT_SIZE[style.subtitleSize]}`}
                >
                  {title.trim() ? <span className={`sy-banner-title ${WEIGHT[style.titleWeight]}`}>{title.trim()}</span> : null}
                  {subtitle.trim() ? <span className="sy-banner-text">{subtitle.trim()}</span> : null}
                  {button ? <span className="sy-banner-cta">{button}</span> : null}
                </span>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
